import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Languages, ChevronDown, Check } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "../lib/i18n";

/**
 * Compact language selector with flag + native name in a dropdown.
 * Persists the user choice to localStorage ("sc_lang") and triggers
 * an Accept-Language refresh so server-side renderings (PDFs, CSVs,
 * activity feed) react immediately.
 */
export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const current = SUPPORTED_LANGUAGES.some((l) => l.code === i18n.language)
    ? i18n.language
    : (i18n.language || "ro").split("-")[0];
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const active = SUPPORTED_LANGUAGES.find((l) => l.code === current) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const onClickAway = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, []);

  const setLang = (lng) => {
    i18n.changeLanguage(lng);
    try { localStorage.setItem("sc_lang", lng); } catch (_) {}
    setOpen(false);
  };

  return (
    <div
      className="lang-switcher"
      data-testid="lang-switcher"
      ref={ref}
      style={{ position: "relative" }}
    >
      <button
        type="button"
        className="lang-btn-trigger"
        onClick={() => setOpen((v) => !v)}
        data-testid="lang-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "0.34rem 0.7rem",
          background: "transparent",
          border: "1px solid rgba(232,201,122,0.5)",
          borderRadius: 6,
          color: "#e8c97a",
          cursor: "pointer",
          fontFamily: "Cinzel, serif",
          fontSize: "0.72rem",
          letterSpacing: "0.14em",
        }}
      >
        <Languages size={13} />
        <span style={{ fontSize: "0.95rem" }}>{active.flag}</span>
        <span>{active.code.toUpperCase()}</span>
        <ChevronDown size={12} style={{ opacity: 0.7, transform: open ? "rotate(180deg)" : "none", transition: "transform 120ms ease" }} />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t("language.label", "Language")}
          data-testid="lang-menu"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: 180,
            background: "#0d1b2a",
            border: "1px solid #c9a84c",
            borderRadius: 6,
            boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
            padding: 4,
            margin: 0,
            listStyle: "none",
            zIndex: 50,
          }}
        >
          {SUPPORTED_LANGUAGES.map((l) => {
            const isActive = l.code === current;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  data-testid={`lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  aria-selected={isActive}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "0.55rem 0.7rem",
                    border: "none",
                    background: isActive ? "rgba(201,168,76,0.18)" : "transparent",
                    color: "#f4e9c8",
                    cursor: "pointer",
                    fontFamily: "Crimson Text, serif",
                    fontSize: "0.92rem",
                    textAlign: "left",
                    borderRadius: 4,
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.background = "rgba(201,168,76,0.10)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span style={{ fontSize: "1.1rem" }}>{l.flag}</span>
                  <span style={{ flex: 1 }}>{l.name}</span>
                  {isActive && <Check size={14} color="#c9a84c" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
